// src/utils/platformOwner.js
// Central platform-owner identity and HTTP admin bridge for Loop.
// Works with Firebase Auth on Web/Android and manualAuth on iOS Capacitor.

import { auth as defaultAuth, db as defaultDb } from "../firebase";
import { httpRequestJson } from "./nativeHttp";
import {
  getEffectiveManualAuthSession,
  getManualAuth,
  isManualAuthLikelyValid,
} from "./manualAuth";

export const LOOP_PLATFORM_OWNER_EMAIL = "juanquiros151@gmail.com";
export const LOOP_PLATFORM_OWNER_UID = "CB3921DGsVcspdnNGL1UCfflegg2";

export function normalizeLoopOwnerEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function clean(value) {
  return String(value || "").trim();
}

function identityFromUser(user, source = "firebase-web") {
  if (!user) return null;
  const uid = clean(user.uid);
  const email = normalizeLoopOwnerEmail(user.email);
  if (!uid && !email) return null;
  return {
    uid: uid || null,
    email,
    displayName: clean(user.displayName || user.name),
    idToken: clean(user.idToken),
    source,
    rawUser: user,
  };
}

function isIosNativeRuntime() {
  try {
    const cap = typeof window !== "undefined" ? window.Capacitor : null;
    const platform = cap && (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform);
    return platform === "ios";
  } catch (_) {
    return false;
  }
}

function identityFromManualSession() {
  try {
    const session = getEffectiveManualAuthSession();
    if (session?.loggedIn && session?.user) {
      const ma = session.manualAuth || getManualAuth();
      return {
        uid: clean(session.user.uid || ma?.uid || ma?.user?.uid) || null,
        email: normalizeLoopOwnerEmail(session.user.email || ma?.email || ma?.user?.email),
        displayName: clean(session.user.displayName || ma?.displayName || ma?.name || ma?.user?.displayName),
        idToken: clean(ma?.idToken),
        source: session.source || ma?.source || "ios-manual",
        rawUser: session.user,
        manualAuth: ma || null,
      };
    }
  } catch (_) {}

  try {
    const ma = getManualAuth();
    if (ma && isManualAuthLikelyValid(ma)) {
      return {
        uid: clean(ma.uid || ma.user?.uid) || null,
        email: normalizeLoopOwnerEmail(ma.email || ma.user?.email),
        displayName: clean(ma.displayName || ma.name || ma.user?.displayName),
        idToken: clean(ma.idToken),
        source: ma.source || "manual-auth",
        rawUser: ma.user || null,
        manualAuth: ma,
      };
    }
  } catch (_) {}

  return null;
}

export function getLoopEffectiveAuthIdentity(authRef = defaultAuth, userFallback = null) {
  // iOS must prefer manualAuth. Firebase Web Auth may hydrate late or expose a
  // stale WebView user, while manualAuth contains the token used by REST/Admin bridges.
  if (isIosNativeRuntime()) {
    const manualIdentity = identityFromManualSession();
    if (manualIdentity) return manualIdentity;
  }

  try {
    const firebaseUser = authRef ? authRef.currentUser : userFallback;
    const firebaseIdentity = identityFromUser(firebaseUser, "firebase-web");
    if (firebaseIdentity) return firebaseIdentity;
  } catch (_) {}

  return null;
}

export function isLoopPlatformOwnerIdentity(identity) {
  if (!identity) return false;
  const uid = clean(identity.uid);
  const email = normalizeLoopOwnerEmail(identity.email);
  return uid === LOOP_PLATFORM_OWNER_UID || email === LOOP_PLATFORM_OWNER_EMAIL;
}

export function isLoopPlatformOwner(authRef = defaultAuth, userFallback = null) {
  return isLoopPlatformOwnerIdentity(getLoopEffectiveAuthIdentity(authRef, userFallback));
}

export async function getLoopEffectiveIdToken(authRef = defaultAuth, userFallback = null) {
  const identity = getLoopEffectiveAuthIdentity(authRef, userFallback);

  // Manual/native identity wins on iOS, matching App/effectiveSession behavior.
  if (identity?.manualAuth && identity?.idToken) return String(identity.idToken);

  try {
    const current = authRef ? authRef.currentUser : userFallback;
    if (current && typeof current.getIdToken === "function") {
      const token = await current.getIdToken();
      if (token && getLoopEffectiveAuthIdentity(authRef, userFallback)?.uid === identity?.uid) return String(token);
    }
  } catch (_) {}





  return "";
}

export function getLoopFunctionsBaseUrl(dbRef = defaultDb) {
  const envBase = String(
    (typeof process !== "undefined" && process.env && (
      process.env.REACT_APP_FUNCTIONS_BASE_URL ||
      process.env.REACT_APP_CLOUD_FUNCTIONS_BASE_URL ||
      process.env.REACT_APP_FIREBASE_FUNCTIONS_BASE_URL
    )) || ""
  ).trim().replace(/\/+$/, "");

  if (envBase) return envBase;

  const projectId = clean(
    dbRef?.app?.options?.projectId ||
    defaultDb?.app?.options?.projectId ||
    (typeof process !== "undefined" ? process.env?.REACT_APP_FIREBASE_PROJECT_ID : "")
  );
  return projectId ? `https://us-central1-${projectId}.cloudfunctions.net` : "";
}

function unwrapOwnerAdminResponse(value) {
  if (!value || typeof value !== "object") return value;
  if (value.data && typeof value.data === "object" && ("ok" in value.data || "error" in value.data || "code" in value.data)) {
    return value.data;
  }
  if (value.body && typeof value.body === "object" && ("ok" in value.body || "error" in value.body || "code" in value.body)) {
    return value.body;
  }
  return value;
}

function serializeOwnerAdminValue(value, depth = 0) {
  if (depth > 20) return null;
  if (value === null || value === undefined) return value ?? null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((item) => serializeOwnerAdminValue(item, depth + 1));

  if (typeof value === "object") {
    const methodName = String(
      value?._methodName ||
      value?._delegate?._methodName ||
      value?._delegate?._methodNameInternal ||
      ""
    ).toLowerCase();
    if (methodName.includes("servertimestamp")) return { __loopServerTimestamp: true };

    if (typeof value.toDate === "function") {
      try { return value.toDate().toISOString(); } catch (_) {}
    }

    const out = {};
    Object.keys(value).forEach((key) => {
      const child = value[key];
      if (typeof child === "function" || child === undefined) return;
      out[key] = serializeOwnerAdminValue(child, depth + 1);
    });
    return out;
  }

  return String(value);
}

export async function callLoopOwnerBusinessAdmin({
  db = defaultDb,
  auth = defaultAuth,
  user = null,
  action,
  businessId = "",
  path = "",
  collectionPath = "",
  payload = null,
  merge = true,
  reason = "",
  limit = null,
} = {}) {
  const identity = getLoopEffectiveAuthIdentity(auth, user);
  if (!isLoopPlatformOwnerIdentity(identity)) {
    const error = new Error("platform_owner_required");
    error.code = "permission-denied";
    throw error;
  }

  const token = await getLoopEffectiveIdToken(auth, user);
  if (!token) {
    const error = new Error("missing_owner_id_token");
    error.code = "unauthenticated";
    throw error;
  }

  const baseUrl = getLoopFunctionsBaseUrl(db);
  if (!baseUrl) throw new Error("missing_functions_base_url");

  const url = `${baseUrl}/ownerBusinessAdminHttp`;
  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
  const data = {
    action: clean(action),
    businessId: clean(businessId),
    path: clean(path),
    collectionPath: clean(collectionPath),
    payload: serializeOwnerAdminValue(payload),
    merge: merge !== false,
    reason: clean(reason),
    limit: limit == null ? null : Number(limit),
    requestId: `owner_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  };

  let result = null;
  const isNative = (() => {
    try {
      const cap = typeof window !== "undefined" ? window.Capacitor : null;
      const platform = cap && (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform);
      return platform === "ios" || platform === "android";
    } catch (_) {
      return false;
    }
  })();

  if (isNative) {
    result = unwrapOwnerAdminResponse(await httpRequestJson({ url, method: "POST", headers, data }));
  } else {
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), 30000) : null;
    let response;
    try {
      response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(data),
        signal: controller?.signal,
      });
    } finally {
      if (timer) clearTimeout(timer);
    }
    let parsed = null;
    try { parsed = await response.json(); } catch (_) {}
    result = unwrapOwnerAdminResponse(parsed || { ok: response.ok });
    if (!response.ok && result?.ok !== false) {
      result = { ...(result || {}), ok: false, code: `http_${response.status}`, error: `HTTP ${response.status}` };
    }
  }

  if (!result || result.ok === false) {
    const error = new Error(result?.error || result?.code || "owner_business_admin_failed");
    error.code = result?.code || "owner_business_admin_failed";
    error.details = result?.details || null;
    throw error;
  }

  return result;
}
